import { api } from '../../../js/api/core/request';

const FALLBACK_FILENAME = 'Clinician Questionnaire.pdf';

/**
 * File name the server chose, from a `Content-Disposition` header — the plain
 * `filename="..."` form, or the encoded `filename*=utf-8''...` one a name with
 * accents arrives in.
 */
export const filenameFromContentDisposition = (disposition: string | undefined): string | null => {
  if (!disposition) return null;
  const encoded = disposition.match(/filename\*=(?:utf-8|UTF-8)''([^;]+)/);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1]);
    } catch {
      return null;
    }
  }
  return disposition.match(/filename="([^"]+)"/)?.[1] ?? null;
};

/**
 * Save a clinician's questionnaire as a PDF (`GET /api/questionnaire/{id}/pdf`).
 * The PDF is generated from the current answers on every request — never a stored
 * copy — and carries who it is about, when the answers were last updated, and when
 * and by whom it was printed. The server names the file (and masks the clinician
 * for a viewer who may not see their name).
 */
export async function downloadQuestionnairePdf(providerId: string): Promise<string> {
  const response = await api.get<Blob>(`/api/questionnaire/${providerId}/pdf`, {
    responseType: 'blob',
  });
  const filename =
    filenameFromContentDisposition(response.headers['content-disposition'] as string) ??
    FALLBACK_FILENAME;
  const url = window.URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(url);
  return filename;
}
